import { useCallback, useRef, useState } from 'react';
import {
  suggestXPath,
  suggestJsonPath,
  type XmlDoc,
  type SuggestTarget,
  type Suggestion,
  type JsonSuggestion,
} from '@capture/shared';

/**
 * Unified suggestion shape. XML uses `{ xpath, description }`; JSON uses
 * `{ path, description }`. We expose the XML shape with `xpath` as the
 * canonical field (JSON path is copied into `xpath`) so the editor UI
 * can treat both uniformly. The original shape is also preserved on
 * `raw` for callers that need to distinguish.
 */
export interface UnifiedSuggestion {
  xpath: string;
  description: string;
  raw: Suggestion | JsonSuggestion;
}

/**
 * React wrapper around the shared `suggestXPath` / `suggestJsonPath`
 * pure functions. Takes the currently parsed sample (XmlDoc for xml,
 * arbitrary JS value for json) plus the format, and returns a stable
 * `suggestForTarget` callback that builds a path from a
 * `SuggestTarget`-shaped selection.
 *
 * `lastSuggestion` tracks the most recent result — handy for chips /
 * status readouts. Reset to null when the root changes.
 */
export function useXPathSuggester(
  root: XmlDoc | unknown | null,
  format: 'xml' | 'json',
) {
  const [lastSuggestion, setLastSuggestion] = useState<UnifiedSuggestion | null>(
    null,
  );
  const rootRef = useRef(root);
  if (rootRef.current !== root) {
    rootRef.current = root;
  }

  const suggestForTarget = useCallback(
    (target: SuggestTarget): UnifiedSuggestion | null => {
      const currentRoot = rootRef.current;
      if (currentRoot === null || currentRoot === undefined) return null;
      if (format === 'xml') {
        const s = suggestXPath(currentRoot as XmlDoc, target);
        const unified: UnifiedSuggestion = {
          xpath: s.xpath,
          description: s.description,
          raw: s,
        };
        setLastSuggestion(unified);
        return unified;
      }
      // JSON — convert string target into a pointer of strings + numbers
      const pointer = jsonPointerFromTarget(target);
      const s = suggestJsonPath(currentRoot, pointer);
      const unified: UnifiedSuggestion = {
        xpath: s.path,
        description: s.description,
        raw: s,
      };
      setLastSuggestion(unified);
      return unified;
    },
    [format],
  );

  return { suggestForTarget, lastSuggestion };
}

/**
 * SampleTreePane emits JSON paths as all-string arrays (`['items', '1',
 * 'total']`). The shared `suggestJsonPath` expects `['items', 1, 'total']`
 * with real numbers for array indices so it can produce `items[1].total`.
 * This helper reverses that lossy conversion by detecting numeric-looking
 * segments.
 */
function jsonPointerFromTarget(
  target: SuggestTarget,
): Array<string | number> {
  const raw: string[] =
    target.kind === 'attr'
      ? [...target.elementPath, target.attrName]
      : target.elementPath;
  return raw.map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg));
}
